import axios from 'axios';

const TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '60000', 10); // 60s for Render free tier cold start

// Ensure baseURL ends with /api
const getBaseUrl = () => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  if (url.endsWith('/')) url = url.slice(0, -1);
  if (!url.endsWith('/api')) url += '/api';
  return url;
};

export const API_BASE_URL = (() => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  if (url.endsWith('/')) url = url.slice(0, -1);
  if (url.endsWith('/api')) url = url.slice(0, -4);
  return url;
})();

// ✅ RETRY CONFIG - Handle cold start gracefully
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 2000, // 2 seconds
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

// Create axios instance
const API = axios.create({
  baseURL: getBaseUrl(),
  timeout: TIMEOUT,
  withCredentials: true  // ⭐ IMPORTANT for CORS + tokens
});

// ✅ Sleep helper for retries
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ✅ REQUEST INTERCEPTOR - Add token to headers & retry logic
let retryCount = {};
API.interceptors.request.use(
  (req) => {
    const token = localStorage.getItem('token');
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
    }
    req.retryCount = retryCount[req.url] || 0;
    return req;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// ✅ RESPONSE INTERCEPTOR - Handle errors & auto-retry on cold start
API.interceptors.response.use(
  (response) => {
    // Reset retry count on success
    if (response.config.url) {
      retryCount[response.config.url] = 0;
    }
    return response;
  },
  async (error) => {
    const { response, code, message, config } = error;

    // ✅ AUTO-RETRY FOR COLD START ERRORS
    const shouldRetry = 
      (code === 'ECONNABORTED' || code === 'ECONNREFUSED' || !response) &&
      config.retryCount < RETRY_CONFIG.maxRetries;
      // Removed method check - retry ALL requests including POST on cold start

    if (shouldRetry) {
      const delay = Math.min(
        RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, config.retryCount),
        RETRY_CONFIG.maxDelay
      );
      
      console.log(`Retrying request (attempt ${config.retryCount + 1}/${RETRY_CONFIG.maxRetries}) after ${delay}ms...`);
      
      await sleep(delay);
      config.retryCount = (config.retryCount || 0) + 1;
      
      return API.request(config);
    }

    // Handle 401 - Token expired or invalid
    // ⚡ Skip guest routes — they are supposed to be unauthenticated
    const isGuestRoute = config?.url?.includes('/auth/guest-init') || config?.url?.includes('/auth/convert-guest');
    if (response?.status === 401 && !isGuestRoute) {
      console.warn('Unauthorized - redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject({
        status: 401,
        message: 'Your session has expired. Please login again.'
      });
    }

    // Handle 403 - Forbidden
    if (response?.status === 403) {
      return Promise.reject({
        status: 403,
        message: 'You do not have permission to access this resource.'
      });
    }

    // Handle 404 - Not found
    if (response?.status === 404) {
      return Promise.reject({
        status: 404,
        message: 'The requested resource was not found.'
      });
    }

    // Handle 500+ - Server errors
    if (response?.status >= 500) {
      return Promise.reject({
        status: response.status,
        message: 'Server error. Please try again later.'
      });
    }

    // Handle network errors
    if (code === 'ECONNABORTED') {
      return Promise.reject({
        status: 0,
        message: 'Server is waking up, please wait a moment and try again (this can take up to 60 seconds).'
      });
    }

    if (!response) {
      // Network error or no response
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your internet connection.'
      });
    }

    // Use backend error message if available
    const backendMessage = response.data?.message || response.data?.error || message;

    return Promise.reject({
      status: response.status,
      message: backendMessage || 'An error occurred. Please try again.'
    });
  }
);

// ✅ WARM UP SERVER ON APP START - Prevents cold start on first request
export const warmupServer = async () => {
  const maxAttempts = 3;
  
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const response = await axios.get(`${getBaseUrl()}/health`, { timeout: 8000 });
      console.log('✅ Server is ready');
      return true;
    } catch (err) {
      if (i < maxAttempts - 1) {
        console.log(`⏳ Warming backend... (attempt ${i + 1}/${maxAttempts})`);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2s before retry
      }
    }
  }
  
  console.log('⚠️ Server warmup timeout - loading in background');
  return false;
};

/**
 * syncToken — Call this immediately after a login/conversion response
 * to inject the new JWT into all future Axios requests without a reload.
 *
 * @param {string} token - The new JWT string
 */
export const syncToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete API.defaults.headers.common['Authorization'];
  }
};

export default API;
